import React from 'react'
import styled from 'styled-components'
import Drawer from '@material-ui/core/Drawer'
import {User} from 'auth/user'
import {useEventAuth} from 'Event/auth'
import CustomButton from 'lib/ui/Button/CustomButton'
import {eventRoutes} from 'Event/Routes'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {useAttendeeVariables} from 'Event'
import {useRequiresPassword} from 'Event/Step1/SetPasswordForm'
import {useLeftyTemplate} from 'Event/template/Lefty'

export const DEFAULT_MENU_TEXT_COLOR = '#FFFFFF'
export const DEFAULT_MENU_ICON_COLOR = '#000000'
export const DEFAULT_MENU_BACKGROUND_COLOR = '#000000'

export default function Menu(props: {
  visible: boolean
  toggle: () => void
  user: User
}) {
  const template = useLeftyTemplate()
  const {menu} = template

  const background = menu?.backgroundColor || DEFAULT_MENU_BACKGROUND_COLOR

  return (
    <Drawer anchor="left" open={props.visible} onClose={props.toggle}>
      <Box background={background}>
        <UserInfo email={props.user.email} />
        <Links />
      </Box>
    </Drawer>
  )
}

function UserInfo(props: {email: string}) {
  const template = useLeftyTemplate()
  const {menu} = template
  const menuTextColor = menu?.textColor || DEFAULT_MENU_TEXT_COLOR
  return (
    <UserInfoText color={menuTextColor}>
      You're logged in as <br />
      {props.email}
    </UserInfoText>
  )
}

function Links() {
  const {logout} = useEventAuth()
  const template = useLeftyTemplate()
  const {menu} = template
  const v = useAttendeeVariables()

  const {
    speakers: {menuTitle: speakerTitle, isEnabled: showingSpeakers},
    sponsors: {isEnabled: showingSponsors, menuTitle: sponsorPageTitle},
    zoomBackgrounds: {
      isEnabled: showingZoomBackgrounds,
      title: zoomBackgroundsTitle,
    },
    leaderboard: {title: pointsTitle, isVisible: showingPoints},
    faq: {title: faqsTitle, isEnabled: showingFaqs},
    imageWaterfall: {
      title: imageWaterfallTitle,
      isVisible: showingImageWaterfall,
    },
  } = template

  const menuTextColor = menu?.textColor || DEFAULT_MENU_TEXT_COLOR

  return (
    <List>
      <ListItem>
        <StyledRelativeLink
          to={eventRoutes.root}
          aria-label="dashboard"
          disableStyles
          color={menuTextColor}
        >
          Dashboard
        </StyledRelativeLink>
      </ListItem>
      <ListItem>
        <Item
          to={eventRoutes.speakers}
          aria-label="menu link speakers"
          color={menuTextColor}
          showing={showingSpeakers}
          text={v(speakerTitle) || 'Our Speakers'}
        />
      </ListItem>
      <ListItem>
        <Item
          to={eventRoutes.sponsors}
          aria-label="menu link sponsors"
          color={menuTextColor}
          showing={showingSponsors}
          text={v(sponsorPageTitle) || 'Our Sponsors'}
        />
      </ListItem>
      <ListItem>
        <Item
          to={eventRoutes.backgrounds}
          aria-label="menu link zoom backgrounds"
          color={menuTextColor}
          showing={showingZoomBackgrounds}
          text={v(zoomBackgroundsTitle)}
        />
      </ListItem>
      <ListItem>
        <Item
          to={eventRoutes.leaderboard}
          aria-label="menu link leaderboard"
          color={menuTextColor}
          showing={showingPoints}
          text={v(pointsTitle)}
        />
      </ListItem>
      <ListItem>
        <Item
          to={eventRoutes.faq}
          aria-label="menu link faqs"
          color={menuTextColor}
          showing={showingFaqs}
          text={v(faqsTitle)}
        />
      </ListItem>
      <ListItem>
        <Item
          to={eventRoutes.image_waterfall}
          aria-label="menu link image waterfall"
          color={menuTextColor}
          showing={showingImageWaterfall}
          text={v(imageWaterfallTitle)}
        />
      </ListItem>
      <ChangePasswordListItem color={menuTextColor} />
      <ListItem>
        <CustomButton
          variant="text"
          onClick={logout}
          aria-label="logout"
          textColor={menuTextColor}
        >
          Logout
        </CustomButton>
      </ListItem>
    </List>
  )
}

function Item(props: {
  to: string
  'aria-label': string
  color: string
  showing: boolean
  text: string
}) {
  if (!props.showing) {
    return null
  }

  return (
    <StyledRelativeLink
      to={props.to}
      aria-label={props['aria-label']}
      disableStyles
      color={props.color}
    >
      {props.text}
    </StyledRelativeLink>
  )
}

function ChangePasswordListItem(props: {color: string}) {
  const requiresPassword = useRequiresPassword()
  if (!requiresPassword) {
    return null
  }

  return (
    <ListItem>
      <StyledRelativeLink
        to={eventRoutes.changePassword}
        aria-label="change password"
        disableStyles
        color={props.color}
      >
        Change Password
      </StyledRelativeLink>
    </ListItem>
  )
}

const Box = styled.div<{background: string}>`
  background: ${(props) => props.background};
  height: 100%;
  min-width: 240px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${(props) =>
    `${props.theme.spacing[20]} ${props.theme.spacing[4]} ${props.theme.spacing[6]}`};
  color: #ffffff;
`

const UserInfoText = styled.p<{color: string}>`
  text-align: center;
  color: ${(props) => props.color} !important;
`

const List = styled.ul`
  list-style: none;
  padding: 0;
`

const ListItem = styled.li`
  margin: ${(props) => props.theme.spacing[2]} 0;
`

const StyledRelativeLink = styled(RelativeLink)<{color: string}>`
  color: ${(props) => props.color};
`
